import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion, useInView } from "framer-motion";
import { 
  Menu, X, Mail, ArrowRight, Check, Shield, FileText, 
  Target, MapPin, Users, Building2, FileCheck, Network,
  ChevronRight, Globe, Lock, ClipboardList, Briefcase,
  AlertTriangle, DollarSign, Search, FileWarning, Settings, Phone
} from "lucide-react";
import { ReadinessCheck } from "@/components/ReadinessCheck";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_complyx-gov/artifacts/7f0lnyqe_transparent.png";
const PHONE_DISPLAY = "(571) 556-4321";
const PHONE_LINK = "tel:+15715564321";

const colors = {
  navy: "#1a2744",
  navyLight: "#243352",
  gold: "#c9a962",
  goldHover: "#d4b86a",
  cream: "#faf9f7",
  white: "#ffffff",
  gray: "#6b7280",
  grayLight: "#9ca3af"
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={fadeIn} className={className}>
      {children}
    </motion.div>
  );
};

// Header
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-white shadow-sm" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <a href="/" data-testid="logo-link" className="flex items-center gap-3 flex-shrink-0">
            <img src={LOGO_URL} alt="ComplyX Gov" className="h-16 sm:h-20 w-auto" />
            <span className="hidden md:block font-mono text-xs font-semibold tracking-widest uppercase" style={{ color: colors.gold }}>Xcelerate Compliance</span>
          </a>
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            <button onClick={() => scrollToSection("services")} className="nav-link font-body text-sm font-medium" style={{ color: colors.gray }}>Services</button>
            <button onClick={() => scrollToSection("pricing")} className="nav-link font-body text-sm font-medium" style={{ color: colors.gray }}>Engagements</button>
            <button onClick={() => scrollToSection("quiz")} className="nav-link font-body text-sm font-medium" style={{ color: colors.gray }}>Readiness Quiz</button>
            <a href={PHONE_LINK} className="hidden xl:flex items-center gap-2 font-body text-sm font-semibold" style={{ color: colors.navy }} data-testid="header-phone">
              <Phone className="w-4 h-4" style={{ color: colors.gold }} />
              {PHONE_DISPLAY}
            </a>
            <a href="https://calendly.com/complyxgov/cmmc-readiness-call" target="_blank" rel="noopener noreferrer" className="font-body text-sm font-medium px-5 py-2.5 rounded transition-colors" style={{ backgroundColor: colors.navy, color: colors.white }}>
              Schedule a Call
            </a>
          </nav>
          <button className="lg:hidden" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} style={{ color: colors.navy }} data-testid="mobile-menu-toggle">
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="lg:hidden bg-white border-t py-6" data-testid="mobile-menu">
          <nav className="flex flex-col gap-4 px-6">
            <button onClick={() => scrollToSection("services")} className="text-left font-body text-sm font-medium py-2" style={{ color: colors.gray }}>Services</button>
            <button onClick={() => scrollToSection("pricing")} className="text-left font-body text-sm font-medium py-2" style={{ color: colors.gray }}>Engagements</button>
            <button onClick={() => scrollToSection("quiz")} className="text-left font-body text-sm font-medium py-2" style={{ color: colors.gray }}>Readiness Quiz</button>
            <a href={PHONE_LINK} className="flex items-center gap-2 py-2 font-body text-sm font-semibold" style={{ color: colors.navy }}>
              <Phone className="w-4 h-4" style={{ color: colors.gold }} />
              Call: {PHONE_DISPLAY}
            </a>
            <a href="https://calendly.com/complyxgov/cmmc-readiness-call" target="_blank" rel="noopener noreferrer" className="text-center font-body text-sm font-medium px-5 py-3 rounded mt-2" style={{ backgroundColor: colors.navy, color: colors.white }}>
              Schedule a Call
            </a>
          </nav>
        </motion.div>
      )}
    </header>
  );
};

// Hero Section - Updated with stronger messaging
const HeroSection = () => {
  const scrollToQuiz = () => document.getElementById("quiz")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="hero-section" className="pt-28 pb-20 lg:pt-36 lg:pb-28" style={{ backgroundColor: colors.navy }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-4xl">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="font-mono text-sm font-semibold tracking-widest uppercase mb-6" style={{ color: colors.gold }}>
            CMMC Level 2 Readiness Support
          </motion.p>
          
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.1] mb-6" style={{ color: colors.white }}>
            Don't Risk Losing DoD Contracts. <span style={{ color: colors.gold }}>We Help You Get CMMC-Ready.</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="font-body text-lg lg:text-xl leading-relaxed mb-10 max-w-3xl" style={{ color: colors.grayLight }}>
            ComplyX Gov helps DoD contractors identify where they may fail CMMC Level 2 readiness, define CUI scope, uncover NIST SP 800-171 gaps, build SSP/POA&M documentation, and create a clear remediation roadmap before assessment.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 mb-8">
            <button onClick={scrollToQuiz} className="inline-flex items-center justify-center rounded px-8 py-4 text-base font-semibold transition-colors" style={{ backgroundColor: colors.gold, color: colors.navy }}>
              Take the Readiness Snapshot
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <a href="https://calendly.com/complyxgov/cmmc-readiness-call" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded px-8 py-4 text-base font-medium border-2 transition-colors" style={{ borderColor: "rgba(255,255,255,0.3)", color: colors.white }}>
              Schedule a 15-Minute Call
            </a>
          </motion.div>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="font-body text-sm" style={{ color: colors.grayLight }}>
            For DoD contractors, subcontractors, and organizations responding to CMMC, NIST 800-171, SPRS, or CUI requirements.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

// Risk/Problem Section - Stronger fear-and-consequence messaging
const RiskSection = () => {
  const risks = [
    {
      icon: AlertTriangle,
      title: "Contract Risk",
      text: "If CMMC requirements show up in a solicitation, subcontractor request, or prime flow-down, your readiness posture can affect whether you can move forward confidently."
    },
    {
      icon: Search,
      title: "Audit Readiness Gaps",
      text: "Many teams do not know where they may fail until someone reviews their controls, documentation, evidence, and CUI boundary against CMMC Level 2 expectations."
    },
    {
      icon: MapPin,
      title: "Unclear CUI Scope",
      text: "If the CUI boundary is wrong, contractors may overbuild, underprotect sensitive data, or waste money securing systems that should not be in scope."
    },
    {
      icon: FileWarning,
      title: "Missing SSP / POA&M",
      text: "A tool stack alone is not enough. Contractors need a defensible System Security Plan, Plan of Action & Milestones, and evidence approach."
    },
    {
      icon: DollarSign,
      title: "Wasted Implementation Spend",
      text: "Buying tools or migrating environments before scoping the requirement can lead to rework, delays, and avoidable cost."
    }
  ];

  return (
    <section id="risk" className="py-20 lg:py-28" style={{ backgroundColor: colors.white }}>
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection className="max-w-3xl mb-16">
          <p className="font-mono text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: colors.gold }}>
            The Reality
          </p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl leading-tight mb-6" style={{ color: colors.navy }}>
            CMMC is not just an IT project. It can become a contract blocker.
          </h2>
          <p className="font-body text-lg leading-relaxed" style={{ color: colors.gray }}>
            Many contractors wait until a prime, agency, solicitation, or survey asks about CMMC, NIST SP 800-171, SPRS, or CUI handling. By then, the issue is no longer theoretical. Unclear scope, missing documentation, weak POA&Ms, and unprepared evidence can delay opportunities, increase remediation costs, or create risk during assessment preparation.
          </p>
        </AnimatedSection>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {risks.map((risk, index) => (
            <motion.div key={index} variants={fadeIn} className="p-6 rounded-lg border flex flex-col h-full" style={{ backgroundColor: colors.cream, borderColor: "rgba(26, 39, 68, 0.1)" }}>
              <risk.icon className="w-10 h-10 mb-4" style={{ color: colors.gold }} strokeWidth={1.5} />
              <h3 className="font-heading font-bold text-lg mb-3" style={{ color: colors.navy }}>{risk.title}</h3>
              <p className="font-body text-sm leading-relaxed" style={{ color: colors.gray }}>{risk.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Services Section - Updated
const ServicesSection = () => {
  const services = [
    { icon: Target, text: "Identify where you may fail CMMC Level 2 readiness" },
    { icon: MapPin, text: "Define the CUI boundary and data flow" },
    { icon: Search, text: "Assess NIST SP 800-171 control gaps" },
    { icon: FileText, text: "Build SSP and POA&M documentation" },
    { icon: ClipboardList, text: "Create a prioritized remediation roadmap" },
    { icon: Users, text: "Prepare leadership and teams for the assessment path" }
  ];

  return (
    <section id="services" className="py-20 lg:py-28" style={{ backgroundColor: colors.white }}>
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection className="text-center mb-16">
          <p className="font-mono text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: colors.gold }}>
            What We Do
          </p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl leading-tight mb-4" style={{ color: colors.navy }}>
            We show you where you stand before you spend.
          </h2>
          <p className="font-body text-lg max-w-3xl mx-auto" style={{ color: colors.gray }}>
            ComplyX Gov helps contractors understand the gap between where they are today and what will be expected for CMMC Level 2 readiness. We focus on readiness assessment, CUI scoping, documentation, POA&M development, and practical remediation planning.
          </p>
        </AnimatedSection>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <motion.div key={index} variants={fadeIn} className="flex items-start gap-4 p-5 rounded-lg" style={{ backgroundColor: colors.cream }}>
              <service.icon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: colors.gold }} strokeWidth={1.5} />
              <span className="font-body font-medium" style={{ color: colors.navy }}>{service.text}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Engagement Pathways Section - Updated
const PricingSection = () => {
  const tiers = [
    {
      tier: "Tier 1",
      name: "CMMC Diagnostic Assessment",
      desc: "Best for contractors that need to understand where they stand before investing in full readiness.",
      note: "Diagnostic only. Not audit-ready.",
      popular: false
    },
    {
      tier: "Tier 2",
      name: "Readiness + Documentation",
      desc: "Best for contractors that need structured readiness support, SSP/POA&M documentation, CUI scoping, and a practical roadmap.",
      note: null,
      popular: true
    },
    {
      tier: "Tier 2.5",
      name: "Readiness + GCC High / Cloud Alignment",
      desc: "Best for contractors aligning cloud usage, GCC High, Microsoft 365, Google Workspace, or enclave design with CMMC expectations.",
      note: null,
      popular: false
    },
    {
      tier: "Tier 3",
      name: "Complex / Multi-System Readiness",
      desc: "Best for contractors with larger environments, multiple systems, complex CUI flows, or multiple stakeholders.",
      note: null,
      popular: false
    }
  ];

  return (
    <section id="pricing" className="py-20 lg:py-28" style={{ backgroundColor: colors.navy }}>
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection className="text-center mb-16">
          <p className="font-mono text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: colors.gold }}>
            Engagement Pathways
          </p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl leading-tight" style={{ color: colors.white }}>
            Choose the right readiness path before investing in implementation.
          </h2>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10 items-stretch">
          {tiers.map((tier, index) => (
            <AnimatedSection key={index} className="h-full">
              <div className={`relative h-full p-6 rounded-lg flex flex-col ${tier.popular ? 'ring-2' : ''}`} style={{ backgroundColor: colors.white, ringColor: colors.gold }}>
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold whitespace-nowrap" style={{ backgroundColor: colors.gold, color: colors.navy }}>
                    Most Common
                  </div>
                )}
                <p className="font-mono text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: colors.gold }}>{tier.tier}</p>
                <h3 className="font-heading font-bold text-lg mb-4 min-h-[3.5rem]" style={{ color: colors.navy }}>{tier.name}</h3>
                <p className="font-body text-sm flex-grow" style={{ color: colors.gray }}>{tier.desc}</p>
                {tier.note && (
                  <p className="font-mono text-xs px-3 py-2 rounded mt-4" style={{ backgroundColor: colors.cream, color: colors.gray }}>{tier.note}</p>
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection>
          <p className="font-body text-sm text-center max-w-3xl mx-auto" style={{ color: colors.grayLight }}>
            Engagement pricing is scoped based on CUI exposure, number of users in scope, documentation maturity, environment complexity, and timeline. The Readiness Snapshot helps identify the right starting point.
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Who We Help Section - Updated
const WhoWeHelpSection = () => {
  const audiences = [
    { icon: Building2, text: "DoD contractors" },
    { icon: Network, text: "DoD subcontractors" },
    { icon: Shield, text: "Companies preparing for CMMC Level 2" },
    { icon: Users, text: "Teams without internal compliance leadership" },
    { icon: FileCheck, text: "Contractors responding to prime or government cybersecurity requests" },
    { icon: Lock, text: "Organizations handling or preparing to handle CUI" }
  ];

  return (
    <section id="who" className="py-20 lg:py-28" style={{ backgroundColor: colors.white }}>
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection className="text-center mb-16">
          <p className="font-mono text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: colors.gold }}>
            Who We Help
          </p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl leading-tight max-w-3xl mx-auto" style={{ color: colors.navy }}>
            Built for contractors who need CMMC direction without hiring a full-time compliance team.
          </h2>
        </AnimatedSection>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {audiences.map((item, index) => (
            <motion.div key={index} variants={fadeIn} className="flex items-center gap-4 p-5 rounded-lg" style={{ backgroundColor: colors.cream }}>
              <item.icon className="w-8 h-8 flex-shrink-0" style={{ color: colors.gold }} strokeWidth={1.5} />
              <span className="font-body font-medium" style={{ color: colors.navy }}>{item.text}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Partner Ecosystem Section
const PartnerSection = () => {
  const steps = [
    { num: "1", title: "ComplyX Gov", desc: "Readiness, scoping, documentation, roadmap" },
    { num: "2", title: "Implementation Support", desc: "Technical remediation and environment alignment when needed" },
    { num: "3", title: "Independent Assessment", desc: "Formal CMMC assessment conducted by an authorized C3PAO when ready" }
  ];

  return (
    <section id="partners" className="py-20 lg:py-28" style={{ backgroundColor: colors.cream }}>
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection className="text-center mb-16">
          <p className="font-mono text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: colors.gold }}>
            Assessment Path
          </p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl leading-tight mb-4" style={{ color: colors.navy }}>
            A clear readiness path before independent assessment.
          </h2>
        </AnimatedSection>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8 items-stretch">
          {steps.map((step, index) => (
            <AnimatedSection key={index} className="h-full">
              <div className="relative text-center p-8 rounded-lg bg-white h-full flex flex-col">
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-6 h-6" style={{ color: colors.gold }} />
                  </div>
                )}
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.navy }}>
                  <span className="font-heading font-bold text-lg" style={{ color: colors.white }}>{step.num}</span>
                </div>
                <h3 className="font-heading font-bold text-lg mb-2" style={{ color: colors.navy }}>{step.title}</h3>
                <p className="font-body text-sm" style={{ color: colors.gray }}>{step.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection>
          <p className="font-body text-xs text-center max-w-3xl mx-auto leading-relaxed" style={{ color: colors.grayLight }}>
            ComplyX Gov does not perform CMMC certification assessments. Formal CMMC assessments must be conducted independently by an authorized C3PAO. Any assessment organization must perform its own conflict-of-interest review before engagement.
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Final CTA Section - Updated
const FinalCTASection = () => {
  const scrollToQuiz = () => document.getElementById("quiz")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="cta" className="py-20 lg:py-28" style={{ backgroundColor: colors.navy }}>
      <div className="max-w-4xl mx-auto px-6 text-center">
        <AnimatedSection>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6" style={{ color: colors.white }}>
            Don't wait until CMMC becomes a contract blocker.
          </h2>
          <p className="font-body text-lg mb-10 max-w-2xl mx-auto" style={{ color: colors.grayLight }}>
            Start with the CMMC Level 2 Readiness Snapshot. It helps identify whether your next step is diagnostic support, documentation, cloud alignment, or assessment preparation.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button onClick={scrollToQuiz} className="w-full sm:w-auto inline-flex items-center justify-center rounded px-8 py-4 text-base font-semibold transition-colors" style={{ backgroundColor: colors.gold, color: colors.navy }}>
              Take the Readiness Snapshot
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <a href="https://calendly.com/complyxgov/cmmc-readiness-call" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center rounded px-8 py-4 text-base font-medium border-2 transition-colors" style={{ borderColor: "rgba(255,255,255,0.3)", color: colors.white }}>
              Schedule a 15-Minute Call
            </a>
          </div>

          <div className="flex items-center justify-center gap-2 pt-6 border-t max-w-md mx-auto" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
            <Phone className="w-5 h-5" style={{ color: colors.gold }} />
            <a href={PHONE_LINK} className="font-body font-semibold" style={{ color: colors.white }} data-testid="cta-phone">
              Or call: {PHONE_DISPLAY}
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-16" style={{ backgroundColor: colors.white, borderTop: `1px solid rgba(26, 39, 68, 0.1)` }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
          <div>
            <img src={LOGO_URL} alt="ComplyX Gov" className="h-20 w-auto mb-4" />
            <p className="font-body text-sm mb-4" style={{ color: colors.gray }}>
              CMMC readiness support for DoD contractors
            </p>
            <div className="space-y-2">
              <a href="https://www.complyxgov.com" className="flex items-center gap-2 font-body text-sm" style={{ color: colors.gray }}>
                <Globe className="w-4 h-4 flex-shrink-0" style={{ color: colors.gold }} />
                www.complyxgov.com
              </a>
              <a href="mailto:info@complyxgov.com" className="flex items-center gap-2 font-body text-sm" style={{ color: colors.gray }}>
                <Mail className="w-4 h-4 flex-shrink-0" style={{ color: colors.gold }} />
                info@complyxgov.com
              </a>
              <a href={PHONE_LINK} className="flex items-center gap-2 font-body text-sm" style={{ color: colors.gray }} data-testid="footer-phone">
                <Phone className="w-4 h-4 flex-shrink-0" style={{ color: colors.gold }} />
                Phone: {PHONE_DISPLAY}
              </a>
            </div>
          </div>

          <div>
            <p className="font-heading font-bold text-sm mb-4" style={{ color: colors.navy }}>Quick Links</p>
            <div className="space-y-2">
              <a href="#services" className="block font-body text-sm" style={{ color: colors.gray }}>Services</a>
              <a href="#pricing" className="block font-body text-sm" style={{ color: colors.gray }}>Engagements</a>
              <a href="#quiz" className="block font-body text-sm" style={{ color: colors.gray }}>Readiness Quiz</a>
              <a href="https://calendly.com/complyxgov/cmmc-readiness-call" target="_blank" rel="noopener noreferrer" className="block font-body text-sm" style={{ color: colors.gray }}>Schedule a Call</a>
            </div>
          </div>

          <div className="md:col-span-2 lg:col-span-1">
            <p className="font-heading font-bold text-sm mb-4" style={{ color: colors.navy }}>Compliance Notice</p>
            <p className="font-body text-xs leading-relaxed" style={{ color: colors.grayLight }}>
              ComplyX Gov provides readiness support, documentation assistance, and compliance preparation services. Formal CMMC certification assessments must be conducted independently by an authorized C3PAO.
            </p>
          </div>
        </div>

        <div className="pt-8 border-t" style={{ borderColor: "rgba(26, 39, 68, 0.1)" }}>
          <p className="font-body text-xs text-center" style={{ color: colors.grayLight }}>
            © {new Date().getFullYear()} ComplyX Gov. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.white }}>
      <Header />
      <main>
        <HeroSection />
        <RiskSection />
        <ReadinessCheck />
        <ServicesSection />
        <PricingSection />
        <WhoWeHelpSection />
        <PartnerSection />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
